import { GIT_COMBINED_STORAGE_MODEL_OPTIONS, VersionControlMode } from '@common/EntityToCreate/createEntityConstants';
import { useFabricProviderOptions } from '@common/Fabric/providerOptions';
import { FabricProviderType } from '@common/Fabric/types';
import { DefaultProjectFormData, useAddOrUpdateTeamProjectCreationTemplate } from '@common/projectHooks';
import {
  BackEndCodeLanguage,
  BackEndCodeLanguageType,
  DefaultTransformationEntity,
  DefaultTransformationEntityType,
  ExternalGitProvider,
  GitCombinedStorageModel,
  GitCombinedStorageModelType,
  GitProvider,
  GitProviderType
} from '@prophecy/graphql-types/enums';
import { HelpCircleIcon } from '@prophecy/icons';
/* eslint-disable react-compiler/react-compiler */
import { theme } from '@prophecy/theme';
import { Button, Input, Select, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { validateWordWithHyphenDotAndSlash } from '@prophecy/utils/validation';
import { SQL_PROVIDER } from 'frontend/core/src/constants/sql';
import { useSupportedGitStorageModels } from 'frontend/core/src/context/edition';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { ProjectTypes } from '../../CreateEntity/Project/common';
import { useGsmOptionsDisabled } from '../../CreateEntity/Project/hooks';
import { ProjectType } from '../../CreateEntity/Project/ProjectType';

const GitProviderOptions = [
  {
    value: GitProvider.External,
    label: 'External'
  },
  {
    value: GitProvider.ProphecyManaged,
    label: 'Prophecy Managed'
  }
];

const HelpIconContainer = styled.div`
  z-index: ${theme.zLayer.xs};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spaces.x16};
  width: 100%;
  overflow: scroll;
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

const FormContainer = styled.form`
  width: 100%;
  overflow: scroll;
  padding: ${theme.spaces.x16};
`;

const ScrollableContainer = styled(Stack)`
  overflow: auto;
  width: 100%;
  height: 100%;

  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const StyledGsmLabel = styled(Stack)`
  padding-right: ${theme.spaces.x8};
  gap: ${theme.spaces.x8};
`;
export enum TemplateCreationMode {
  Add = 'add',
  Edit = 'edit'
}

const DefaultProjectSettings = ({
  teamId,
  mode,
  fetchedDefaultValues,
  uid: _uid,
  closeDialog,
  onTemplateUpdate
}: {
  teamId: string;
  mode: TemplateCreationMode;
  fetchedDefaultValues?: DefaultProjectFormData;
  uid?: number;
  closeDialog?: () => void;
  onTemplateUpdate?: () => void;
}) => {
  const onSuccess = () => {
    toast.success({
      title: 'Template saved successfully',
      content: 'Template settings have been saved successfully'
    });
    onTemplateUpdate?.();
  };

  const { addOrUpdateTeamProjectCreationTemplate, isLoading } = useAddOrUpdateTeamProjectCreationTemplate(onSuccess);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm<DefaultProjectFormData>({
    defaultValues: fetchedDefaultValues
  });

  const { isSapEnabled, getGitProviderOptions } = useGlobalVariables();

  useEffect(() => {
    if (fetchedDefaultValues) {
      reset(fetchedDefaultValues);
    }
  }, [fetchedDefaultValues, reset]);

  const onSubmit = (data: DefaultProjectFormData) => {
    const showMainBranch = isExternalGitProvider || !isSimpleMode;
    const commonVariables: {
      language: BackEndCodeLanguageType;
      gitProviderType: GitProviderType;
      sqlProviderType?: string;
      versionControlMode?: VersionControlMode;
      gitCombinedStorageModel: GitCombinedStorageModelType;
      defaultMainBranch?: string;
      defaultDevelopmentBranch?: string;
      allowToCustomize: boolean;
      isDefault: boolean;
      name: string;
      defaultTransformationEntity?: DefaultTransformationEntityType;
    } = {
      language: data?.language as BackEndCodeLanguageType,
      gitProviderType: data?.gitProviderType,
      gitCombinedStorageModel: data?.gitCombinedStorageModel,
      sqlProviderType: isSqlProject ? data?.sqlProviderType || '' : '',
      versionControlMode: isSqlProject ? data?.versionControlType : VersionControlMode.Advanced,
      defaultMainBranch: showMainBranch ? data?.defaultMainBranch : '',
      defaultDevelopmentBranch: isExternalGitProvider ? data?.defaultDevelopmentBranch || '' : '',
      allowToCustomize: data?.allowToCustomize,
      isDefault: data?.isDefault || false,
      name: mode === TemplateCreationMode.Edit ? fetchedDefaultValues?.name || '' : data?.name,
      defaultTransformationEntity: data?.defaultTransformationEntity
    };
    // oxlint-disable-next-line no-unused-expressions
    !isSqlProject && delete commonVariables?.sqlProviderType;
    if (!isExternalGitProvider) {
      // oxlint-disable-next-line no-unused-expressions
      isSimpleMode && delete commonVariables?.defaultMainBranch;
      delete commonVariables?.defaultDevelopmentBranch;
    }

    if (teamId) {
      addOrUpdateTeamProjectCreationTemplate({
        uid: fetchedDefaultValues?.uid ? `${fetchedDefaultValues?.uid}` : undefined,
        teamUID: teamId,
        ...commonVariables
      }).finally(() => {
        closeDialog?.();
      });
    }
  };

  const language = watch('language');
  const isSimpleMode = watch('gitCombinedStorageModel') === GitCombinedStorageModel.Simple;
  const isPySpark = language === BackEndCodeLanguage.python;
  const isPySparkSimplified = language === BackEndCodeLanguage.python && isSimpleMode;
  const isSqlProject = language === BackEndCodeLanguage.sql || isPySparkSimplified;
  const isExternalGitProvider = watch('gitProviderType') === GitProvider.External;

  useEffect(() => {
    if (isPySparkSimplified) {
      setValue('sqlProviderType', SQL_PROVIDER.DataBricks);
    }
  }, [isPySparkSimplified, setValue]);

  const supportedGitStorageModels = useSupportedGitStorageModels();
  const disabledGsmOptions = useGsmOptionsDisabled(isSqlProject || isPySpark, !isExternalGitProvider, false);
  const sqlProviderOptions = useFabricProviderOptions(FabricProviderType.SQL, true);

  const isProphecyManagedGitAvailable =
    !getGitProviderOptions ||
    (getGitProviderOptions as ExternalGitProvider[]).includes(ExternalGitProvider.ProphecyManaged);

  const availableGitProviderOptions = isProphecyManagedGitAvailable
    ? GitProviderOptions
    : GitProviderOptions.filter((option) => option.value !== GitProvider.ProphecyManaged);

  useEffect(() => {
    if (!isProphecyManagedGitAvailable) {
      setValue('gitProviderType', GitProvider.External);
    }
  }, [fetchedDefaultValues, isProphecyManagedGitAvailable, setValue]);

  return (
    <ScrollableContainer direction='horizontal' gap={theme.spaces.x48}>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={theme.spaces.x12}>
          {mode === TemplateCreationMode.Add ? (
            <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
              Create Template
            </Text>
          ) : null}
          <FormGrid>
            <FullWidthField>
              {mode === TemplateCreationMode.Add ? (
                <Controller
                  render={({ field }) => (
                    <Input
                      label='Template Name'
                      {...field}
                      placeholder='Enter Name'
                      variant={errors.name ? 'error' : 'primary'}
                      helpText={errors.name?.message}
                      data-test-id='default-project-settings-template-name-input'
                    />
                  )}
                  control={control}
                  name='name'
                  rules={{ required: 'Name is required' }}
                />
              ) : null}
            </FullWidthField>
            <FullWidthField>
              <Controller
                render={({ field }) => (
                  <ProjectType
                    label={field.value ? ProjectTypes[field.value]?.label : ''}
                    {...field}
                    disableSql={false}
                    disabled={false}
                    width='320px'
                    size='lg'
                  />
                )}
                control={control}
                name='language'
                rules={{
                  required: 'Language is required'
                }}
              />
              {errors.language ? (
                <Text level='xs' weight={theme.fontWeight.medium} tone={theme.colors.error500}>
                  Project Type is required
                </Text>
              ) : null}
            </FullWidthField>

            <Controller
              render={({ field }) => (
                <Select
                  label='Allowed to customize'
                  value={field.value}
                  variant={errors.allowToCustomize ? 'error' : 'primary'}
                  helpText={errors.allowToCustomize?.message}
                  onChange={(value) => field.onChange(value)}
                  data-test-id='default-project-settings-allowed-to-customize-select'
                  placeholder='Select customization option'>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              )}
              control={control}
              name='allowToCustomize'
              rules={{
                validate: (value) => (value !== undefined && value !== null) || 'Allowed to customize is required'
              }}
            />

            <Controller
              render={({ field }) => (
                <Select
                  label='Select as Default template'
                  value={field.value}
                  variant={errors.isDefault ? 'error' : 'primary'}
                  helpText={errors.isDefault?.message}
                  onChange={(value) => field.onChange(value)}
                  placeholder='Select Default template'
                  data-test-id='default-project-settings-select-as-default-template-select'>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              )}
              rules={{
                validate: (value) => (value !== undefined && value !== null) || 'Select as Default template is required'
              }}
              control={control}
              name='isDefault'
            />

            {isSqlProject && !isSapEnabled && (
              <>
                <Controller
                  render={({ field }) => (
                    <Select
                      label='SQL Provider'
                      {...field}
                      disabled={isPySparkSimplified}
                      variant={errors.sqlProviderType ? 'error' : 'primary'}
                      placeholder='Select SQL Provider'
                      data-test-id='default-project-settings-sql-provider-select'
                      helpText={errors.sqlProviderType?.message}
                      tooltip={
                        isPySparkSimplified ? 'PySpark Simplified only supports Databricks provider' : undefined
                      }>
                      {sqlProviderOptions.map((item, index) => (
                        <Select.Option key={index} value={item.value}>
                          <Stack direction='horizontal' align='start' gap={theme.spaces.x8}>
                            {item.icon}
                            {item.label}
                          </Stack>
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                  control={control}
                  name='sqlProviderType'
                  rules={{
                    required: isSqlProject ? 'SQL Provider is required' : undefined
                  }}
                />
              </>
            )}

            <Controller
              render={({ field }) => (
                <Select
                  label='Git Provider'
                  {...field}
                  disabled={!isProphecyManagedGitAvailable}
                  placeholder='Select Git Provider'
                  variant={errors.gitProviderType ? 'error' : 'primary'}
                  helpText={errors.gitProviderType?.message}
                  options={availableGitProviderOptions}
                  data-test-id='default-project-settings-git-provider-select'
                />
              )}
              control={control}
              name='gitProviderType'
              rules={{
                required: 'Git Provider is required'
              }}
            />

            {!isSimpleMode || isExternalGitProvider ? (
              <Controller
                render={({ field }) => (
                  <Input
                    label='Default Main Branch'
                    {...field}
                    placeholder='Enter Main Branch Name'
                    variant={errors.defaultMainBranch ? 'error' : 'primary'}
                    helpText={errors.defaultMainBranch?.message}
                    data-test-id='default-project-settings-default-main-branch-input'
                  />
                )}
                control={control}
                name='defaultMainBranch'
                rules={{
                  required: 'Default Main Branch is required',
                  validate: validateWordWithHyphenDotAndSlash
                }}
              />
            ) : null}

            <>
              <Controller
                render={({ field }) => (
                  <Select
                    label='Git Storage Model'
                    {...field}
                    disabled={false}
                    variant={errors.gitCombinedStorageModel ? 'error' : 'primary'}
                    helpText={errors.gitCombinedStorageModel?.message}
                    data-test-id='default-project-settings-git-storage-model-select'>
                    {GIT_COMBINED_STORAGE_MODEL_OPTIONS?.map((item, index) => {
                      const notSupported = !supportedGitStorageModels.has(item.value as GitCombinedStorageModel);
                      const isDisabled =
                        disabledGsmOptions.includes(item.value as GitCombinedStorageModel) || notSupported;
                      return (
                        <Select.Option key={index} disabled={isDisabled} value={item.value}>
                          <StyledGsmLabel direction='horizontal' alignY='center' align='space-between'>
                            {item.label}
                            {item.tooltip && (
                              <Tooltip
                                title={
                                  isDisabled
                                    ? notSupported
                                      ? 'Not supported for this environment'
                                      : item?.disabledToolTip
                                    : item.tooltip
                                }>
                                <HelpIconContainer>
                                  <HelpCircleIcon type='default' size='xs' />
                                </HelpIconContainer>
                              </Tooltip>
                            )}
                          </StyledGsmLabel>
                        </Select.Option>
                      );
                    })}
                  </Select>
                )}
                control={control}
                name='gitCombinedStorageModel'
                rules={{
                  required: 'Git Storage Model is required'
                }}
              />
              {isExternalGitProvider && isSimpleMode && (
                <Controller
                  render={({ field }) => (
                    <Input
                      label='Default Development Branch'
                      {...field}
                      placeholder='Enter Development Branch Name'
                      variant={errors.defaultDevelopmentBranch ? 'error' : 'primary'}
                      helpText={errors.defaultDevelopmentBranch?.message}
                      data-test-id='default-project-settings-default-development-branch-input'
                    />
                  )}
                  control={control}
                  name='defaultDevelopmentBranch'
                  rules={{
                    required: isExternalGitProvider ? 'Default Development Branch is required' : undefined,
                    validate: validateWordWithHyphenDotAndSlash
                  }}
                />
              )}
            </>
            {isSqlProject && (
              <Controller
                render={({ field }) => (
                  <Select
                    label='Default Transformation Entity'
                    {...field}
                    placeholder='Select Default Transformation Entity'
                    variant={errors.defaultTransformationEntity ? 'error' : 'primary'}
                    helpText={errors.defaultTransformationEntity?.message}
                    data-test-id='default-project-settings-default-transformation-entity-select'>
                    <Select.Option value={DefaultTransformationEntity.Model}>Model</Select.Option>
                    <Select.Option value={DefaultTransformationEntity.Pipeline}>Pipeline</Select.Option>
                  </Select>
                )}
                control={control}
                name='defaultTransformationEntity'
                rules={{
                  required: 'Default Transformation Entity is required'
                }}
              />
            )}
          </FormGrid>

          <Stack direction='horizontal' align='end' padding={`${theme.spaces.x32} 0 0 0`}>
            <Button
              loading={isLoading}
              type='submit'
              variant='primary'
              data-test-id='default-project-settings-submit-button'>
              {mode === TemplateCreationMode.Add ? 'Create Template' : 'Update Template'}
            </Button>
          </Stack>
        </Stack>
      </FormContainer>
    </ScrollableContainer>
  );
};

export default DefaultProjectSettings;
